import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  DEFAULT_CONSENT_FLAGS,
  readConsentCookie,
  writeConsentCookie,
} from "./consentCookie";
import { getButtonClassName, surfaceStyles } from "../ui/styles";

const ConsentContext = createContext(null);

function ConsentModal({
  open,
  draft,
  onClose,
  onAccept,
  onReject,
  onDraftChange,
  onSaveDraft,
  showDetails,
  setShowDetails,
  focusCategory,
}) {
  const dialogTitleRef = useRef(null);
  const externalMediaToggleRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (focusCategory === "externalMedia" && externalMediaToggleRef.current) {
      externalMediaToggleRef.current.focus();
      return;
    }
    dialogTitleRef.current?.focus();
  }, [open, focusCategory]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-dialog-title"
        className={`w-full max-w-xl p-4 ${surfaceStyles.modal}`}
      >
        <h2
          id="cookie-dialog-title"
          ref={dialogTitleRef}
          tabIndex={-1}
          className="text-lg font-semibold text-text focus:outline-none"
        >
          Informasjonskapsler
        </h2>
        <p className="mt-2 text-sm text-text">
          Vi bruker ingen analyse- eller markedsføringskapsler i gjestemodus.
          Eksterne bilder er av som standard og kan aktiveres frivillig.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onAccept}
            className={getButtonClassName({
              variant: "primary",
              size: "md",
              shape: "control",
            })}
          >
            Godta
          </button>
          <button
            type="button"
            onClick={onReject}
            className={getButtonClassName({
              variant: "danger",
              size: "md",
              shape: "control",
            })}
          >
            Avvis
          </button>
        </div>

        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowDetails((prev) => !prev)}
            className="text-sm font-medium text-text underline underline-offset-2"
          >
            {showDetails ? "Skjul detaljer" : "Tilpass valg"}
          </button>
        </div>

        {showDetails && (
          <div className={`mt-4 space-y-3 p-3 ${surfaceStyles.data}`}>
            <label className="flex items-start justify-between gap-3">
              <span>
                <span className="block text-sm font-semibold text-text">
                  Eksterne bilder
                </span>
                <span className="block text-xs text-text">
                  Tillat innlasting av bilder fra eksterne nettsteder.
                </span>
              </span>
              <input
                ref={externalMediaToggleRef}
                type="checkbox"
                checked={Boolean(draft.externalMedia)}
                onChange={(event) =>
                  onDraftChange((prev) => ({
                    ...prev,
                    externalMedia: event.target.checked,
                  }))
                }
              />
            </label>

            <label className="flex items-start justify-between gap-3 opacity-70">
              <span>
                <span className="block text-sm font-semibold text-text">
                  Analyse
                </span>
                <span className="block text-xs text-text">
                  Ikke i bruk i MVP. Forblir av.
                </span>
              </span>
              <input
                type="checkbox"
                checked={false}
                disabled
                aria-disabled="true"
              />
            </label>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className={getButtonClassName({
                  variant: "secondary",
                  size: "sm",
                  shape: "control-sm",
                })}
              >
                Lukk
              </button>
              <button
                type="button"
                onClick={onSaveDraft}
                className={getButtonClassName({
                  variant: "secondary",
                  size: "sm",
                  shape: "control-sm",
                })}
              >
                Lagre valg
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ConsentProvider({ children }) {
  const initial = readConsentCookie();
  const [consentFlags, setConsentFlags] = useState(
    initial?.flags || DEFAULT_CONSENT_FLAGS,
  );
  const [hasChoice, setHasChoice] = useState(Boolean(initial));
  const [modalOpen, setModalOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [focusCategory, setFocusCategory] = useState(null);
  const [draft, setDraft] = useState(initial?.flags || DEFAULT_CONSENT_FLAGS);
  const previousFocusRef = useRef(null);

  const restoreFocus = useCallback(() => {
    const previous = previousFocusRef.current;
    if (previous && typeof previous.focus === "function") {
      previous.focus();
    }
    previousFocusRef.current = null;
  }, []);

  const closeSettings = useCallback(() => {
    setModalOpen(false);
    setShowDetails(false);
    setFocusCategory(null);
    restoreFocus();
  }, [restoreFocus]);

  const persistConsent = useCallback(
    (nextFlags) => {
      const normalized = {
        externalMedia: Boolean(nextFlags?.externalMedia),
        analytics: false,
      };
      writeConsentCookie(normalized);
      setConsentFlags(normalized);
      setDraft(normalized);
      setHasChoice(true);
      closeSettings();
    },
    [closeSettings],
  );

  const openSettings = useCallback(
    ({ focus = null, trigger = null } = {}) => {
      if (typeof document !== "undefined") {
        previousFocusRef.current =
          trigger ||
          (document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null);
      }
      setDraft(consentFlags);
      setFocusCategory(focus);
      setShowDetails(Boolean(focus));
      setModalOpen(true);
    },
    [consentFlags],
  );

  const acceptAll = useCallback(
    () => persistConsent({ externalMedia: true, analytics: false }),
    [persistConsent],
  );

  const rejectAll = useCallback(
    () => persistConsent({ externalMedia: false, analytics: false }),
    [persistConsent],
  );

  const saveDraft = useCallback(() => {
    persistConsent(draft);
  }, [draft, persistConsent]);

  const value = useMemo(
    () => ({
      hasChoice,
      consentFlags,
      externalMediaEnabled: Boolean(consentFlags.externalMedia),
      openSettings,
      acceptAll,
      rejectAll,
      saveDraft,
    }),
    [acceptAll, consentFlags, hasChoice, openSettings, rejectAll, saveDraft],
  );

  return (
    <ConsentContext.Provider value={value}>
      {children}
      <ConsentModal
        open={modalOpen}
        draft={draft}
        onClose={closeSettings}
        onAccept={acceptAll}
        onReject={rejectAll}
        onDraftChange={setDraft}
        onSaveDraft={saveDraft}
        showDetails={showDetails}
        setShowDetails={setShowDetails}
        focusCategory={focusCategory}
      />
    </ConsentContext.Provider>
  );
}

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (context) return context;
  return {
    hasChoice: false,
    consentFlags: DEFAULT_CONSENT_FLAGS,
    externalMediaEnabled: false,
    openSettings: () => {},
    acceptAll: () => {},
    rejectAll: () => {},
    saveDraft: () => {},
  };
};
